import React, { useState, useEffect, useRef } from 'react';

export default function DiscoverEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    date: 'all',
    area: 'all',
    price: 'all'
  });

  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Mock event data
  const mockEvents = [
    {
      id: 1,
      title: "Morning Coffee & Professional Networking",
      date: "2025-01-25",
      time: "09:00",
      location: "The Coffee House, City Centre",
      area: "City Centre",
      price: "Free",
      type: "Networking",
      description: "Join local professionals for casual morning networking over expertly crafted coffee. Connect with entrepreneurs, freelancers, and business leaders in a relaxed atmosphere.",
      imageUrl: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop",
      hostName: "Sarah Mitchell",
      hostBusiness: "The Coffee House",
      currentAttendees: 23,
      capacity: 30,
      featured: true,
      tags: ["#coffee", "#networking", "#business"]
    },
    {
      id: 2,
      title: "Creative Digital Marketing Workshop",
      date: "2025-01-27",
      time: "14:00",
      location: "Creative Studio, Beeston",
      area: "Beeston",
      price: "£15",
      type: "Workshop",
      description: "Learn the latest digital marketing strategies specifically designed for small businesses. Hands-on workshop with practical takeaways.",
      imageUrl: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=400&h=300&fit=crop",
      hostName: "James Wilson",
      hostBusiness: "Creative Studio Nottingham",
      currentAttendees: 15,
      capacity: 25,
      featured: false,
      tags: ["#marketing", "#workshop", "#digital"]
    },
    {
      id: 3,
      title: "Local Authors Book Reading",
      date: "2025-01-28",
      time: "19:00",
      location: "The Book Nook, West Bridgford",
      area: "West Bridgford",
      price: "Free",
      type: "Cultural",
      description: "Evening with local Nottingham authors reading from their latest works. Wine and light refreshments provided.",
      imageUrl: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=300&fit=crop",
      hostName: "Emma Thompson",
      hostBusiness: "The Book Nook",
      currentAttendees: 18,
      capacity: 40,
      featured: true,
      tags: ["#books", "#culture", "#reading"]
    },
    {
      id: 4,
      title: "Small Business Finance Workshop",
      date: "2025-01-30",
      time: "18:30",
      location: "Business Hub, Nottingham",
      area: "City Centre",
      price: "£25",
      type: "Workshop",
      description: "Essential finance tips for small business owners. Covering cash flow, accounting basics, and funding options.",
      imageUrl: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop",
      hostName: "Michael Chen",
      hostBusiness: "Business Hub Nottingham",
      currentAttendees: 12,
      capacity: 20,
      featured: false,
      tags: ["#finance", "#business", "#workshop"]
    },
    {
      id: 5,
      title: "Sustainable Business Practices Meet-up",
      date: "2025-02-02",
      time: "12:00",
      location: "Green Cafe, Hockley",
      area: "Hockley",
      price: "Free",
      type: "Networking",
      description: "Connect with environmentally conscious business owners. Share ideas on sustainable practices and eco-friendly initiatives.",
      imageUrl: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=400&h=300&fit=crop",
      hostName: "Lucy Green",
      hostBusiness: "Green Cafe",
      currentAttendees: 8,
      capacity: 15,
      featured: false,
      tags: ["#sustainability", "#networking", "#eco"]
    },
    {
      id: 6,
      title: "Tech Innovation Showcase",
      date: "2025-02-05",
      time: "17:00",
      location: "Innovation Centre, Nottingham",
      area: "City Centre",
      price: "Free",
      type: "Exhibition",
      description: "Local tech startups showcase their latest innovations. Great for networking and discovering new technologies.",
      imageUrl: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&h=300&fit=crop",
      hostName: "Tech Hub Nottingham",
      hostBusiness: "Innovation Centre",
      currentAttendees: 45,
      capacity: 100,
      featured: true,
      tags: ["#tech", "#innovation", "#startup"]
    }
  ];

  const eventTypes = ["Workshop", "Networking", "Cultural", "Exhibition", "Market", "Class", "Other"];
  const areas = ["City Centre", "Beeston", "West Bridgford", "Hockley"];

  useEffect(() => {
    loadEvents();
  }, [filters]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      let filteredEvents = mockEvents;

      // Apply search filter
      if (filters.search) {
        filteredEvents = filteredEvents.filter(event =>
          event.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          event.description.toLowerCase().includes(filters.search.toLowerCase()) ||
          event.location.toLowerCase().includes(filters.search.toLowerCase()) ||
          event.hostName.toLowerCase().includes(filters.search.toLowerCase()) ||
          event.hostBusiness.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      // Apply type filter
      if (filters.type !== 'all') {
        filteredEvents = filteredEvents.filter(event => event.type === filters.type);
      }

      // Apply area filter
      if (filters.area !== 'all') {
        filteredEvents = filteredEvents.filter(event => event.area === filters.area);
      }

      // Apply price filter
      if (filters.price !== 'all') {
        if (filters.price === 'free') {
          filteredEvents = filteredEvents.filter(event => event.price === 'Free');
        } else if (filters.price === 'paid') {
          filteredEvents = filteredEvents.filter(event => event.price !== 'Free');
        }
      }

      // Apply date filter
      if (filters.date !== 'all') {
        const today = new Date();
        const eventDate = new Date();
        
        if (filters.date === 'today') {
          eventDate.setDate(today.getDate());
        } else if (filters.date === 'week') {
          eventDate.setDate(today.getDate() + 7);
        } else if (filters.date === 'month') {
          eventDate.setMonth(today.getMonth() + 1);
        }
        
        filteredEvents = filteredEvents.filter(event => {
          const eventDateTime = new Date(event.date);
          return filters.date === 'today' 
            ? eventDateTime.toDateString() === today.toDateString()
            : eventDateTime <= eventDate;
        });
      }

      // Sort featured events first
      filteredEvents.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return new Date(a.date) - new Date(b.date);
      });

      setEvents(filteredEvents);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      date: 'all',
      area: 'all',
      price: 'all'
    });
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleRSVP = async (eventId, formData) => {
    try {
      // Simulate RSVP submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update attendee count
      setEvents(prev => prev.map(event => 
        event.id === eventId 
          ? { ...event, currentAttendees: event.currentAttendees + 1 }
          : event
      ));

      // Show success message
      alert('RSVP successful! Thank you for registering.');
      closeEventModal();
    } catch (error) {
      alert('Failed to submit RSVP. Please try again.');
    }
  };

  const hasActiveFilters = () => {
    return filters.search || filters.type !== 'all' || filters.date !== 'all' || 
           filters.area !== 'all' || filters.price !== 'all';
  };

  return (
    
      <div ref={sectionRef} className="min-h-screen bg-slate-50 p-6">
        {/* Header */}
        <div className={`mb-8 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Discover <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Local Events</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Find amazing events happening in Nottingham. Connect with your community 
              and discover new experiences at local businesses.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search events, venues, or keywords..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg border border-slate-300 rounded-xl bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              />
              <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-xl"></i>
            </div>
          </div>

          {/* Quick Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {['Today', 'This Week', 'Free', 'Workshops', 'Networking'].map((filter) => (
              <button
                key={filter}
                onClick={() => {
                  if (filter === 'Today') handleFilterChange('date', 'today');
                  else if (filter === 'This Week') handleFilterChange('date', 'week');
                  else if (filter === 'Free') handleFilterChange('price', 'free');
                  else if (filter === 'Workshops') handleFilterChange('type', 'Workshop');
                  else if (filter === 'Networking') handleFilterChange('type', 'Networking');
                }}
                className="px-4 py-2 bg-white border border-slate-300 text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 rounded-lg transition-colors"
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className={`bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{animationDelay: '0.2s'}}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            {/* Event Type Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Event Type</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                {eventTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">When</label>
              <select
                value={filters.date}
                onChange={(e) => handleFilterChange('date', e.target.value)}
                className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>

            {/* Area Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Area</label>
              <select
                value={filters.area}
                onChange={(e) => handleFilterChange('area', e.target.value)}
                className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Areas</option>
                {areas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            {/* Price Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Price</label>
              <select
                value={filters.price}
                onChange={(e) => handleFilterChange('price', e.target.value)}
                className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Prices</option>
                <option value="free">Free</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex space-x-2">
              {hasActiveFilters() && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Clear all filters"
                >
                  <i className="fas fa-times mr-2"></i>
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters() && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200">
              <span className="text-sm text-slate-600">Active filters:</span>
              {filters.search && (
                <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                  Search: "{filters.search}"
                </span>
              )}
              {filters.type !== 'all' && (
                <span className="bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full">
                  Type: {filters.type}
                </span>
              )}
              {filters.date !== 'all' && (
                <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                  Date: {filters.date}
                </span>
              )}
              {filters.area !== 'all' && (
                <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                  Area: {filters.area}
                </span>
              )}
              {filters.price !== 'all' && (
                <span className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1 rounded-full">
                  Price: {filters.price}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className={`text-center py-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{animationDelay: '0.4s'}}>
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-slate-600">Finding amazing events for you...</p>
          </div>
        )}

        {/* Events Grid */}
        {!loading && (
          <div className={`transition-opacity duration-100 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{animationDelay: '0.4s'}}>
            {/* Results Count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-slate-600">
                {events.length === 0 
                  ? 'No events found' 
                  : `${events.length} event${events.length !== 1 ? 's' : ''} found`
                }
              </p>
              
              {events.some(event => event.featured) && (
                <div className="flex items-center text-sm text-slate-600">
                  <div className="w-3 h-3 bg-purple-600 rounded-full mr-2"></div>
                  Featured events from Basic Paid subscribers
                </div>
              )}
            </div>

            {events.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-slate-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <i className="fas fa-calendar-times text-slate-400 text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No events found</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  {hasActiveFilters()
                    ? 'Try adjusting your search criteria or clear the filters to see all events.'
                    : 'No events are currently available. Check back soon for new events!'
                  }
                </p>
                {hasActiveFilters() && (
                  <button
                    onClick={clearFilters}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    <i className="fas fa-refresh mr-2"></i>
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event, index) => (
                  <div
                    key={event.id}
                    className={`transition-all duration-300 ${isVisible ? 'animate-slideInUp' : 'opacity-0 translate-y-4'}`}
                    style={{animationDelay: `${index * 0.1}s`}}
                  >
                    <EventCard
                      event={event}
                      onClick={() => openEventModal(event)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Call to Action for Event Hosts */}
        <div className={`mt-16 bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/20 rounded-xl p-8 text-center ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{animationDelay: '0.8s'}}>
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mx-auto mb-6 flex items-center justify-center">
            <i className="fas fa-plus text-white text-2xl"></i>
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-4">Host Your Own Event</h3>
          <p className="text-slate-700 mb-6 max-w-2xl mx-auto">
            Are you a Nottingham business owner? Join TrentConnect and start hosting events 
            to connect with your local community. It's free to get started!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.location.href = '/create-event'}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-all transform hover:scale-105"
            >
              <i className="fas fa-rocket mr-2"></i>
              Start Hosting Events
            </button>
            <button 
              onClick={() => window.location.href = '/ai-assistant'}
              className="border border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-900 px-8 py-3 rounded-lg font-medium transition-colors"
            >
              <i className="fas fa-robot mr-2"></i>
              Try AI Assistant
            </button>
          </div>
        </div>

        {/* Event Details Modal */}
        {selectedEvent && (
          <EventDetailsModal
            event={selectedEvent}
            onClose={closeEventModal}
            onRSVP={handleRSVP}
          />
        )}
      </div>
    
  );
}

// Event Card Component
function EventCard({ event, onClick }) {
  const eventDate = new Date(event.date);
  const isUpcoming = eventDate >= new Date();
  const spotsLeft = event.capacity - event.currentAttendees;

  return (
    <div 
      className="bg-white rounded-xl border border-slate-200 overflow-hidden cursor-pointer group hover:shadow-lg transition-all duration-200 relative"
      onClick={onClick}
    >
      {/* Featured Badge */}
      {event.featured && (
        <div className="absolute top-4 right-4 z-10">
          <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">
            FEATURED
          </span>
        </div>
      )}

      {/* Event Image */}
      <div className="aspect-video bg-slate-200 overflow-hidden">
        {event.imageUrl ? (
          <img 
            src={event.imageUrl} 
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-full h-full ${event.imageUrl ? 'hidden' : 'flex'} items-center justify-center`}>
          <i className="fas fa-calendar-alt text-slate-400 text-3xl"></i>
        </div>
      </div>

      {/* Event Details */}
      <div className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
            {event.title}
          </h3>
          <p className="text-sm text-slate-600 line-clamp-2">{event.description}</p>
        </div>

        <div className="space-y-2 text-sm text-slate-600">
          <div className="flex items-center">
            <i className="fas fa-calendar mr-3 w-4 text-blue-600"></i>
            <span>{eventDate.toLocaleDateString('en-GB', { 
              weekday: 'short',
              month: 'short', 
              day: 'numeric' 
            })}</span>
          </div>
          
          <div className="flex items-center">
            <i className="fas fa-clock mr-3 w-4 text-blue-600"></i>
            <span>{event.time}</span>
          </div>
          
          <div className="flex items-center">
            <i className="fas fa-map-marker-alt mr-3 w-4 text-blue-600"></i>
            <span className="line-clamp-1">{event.location}</span>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center">
              <i className="fas fa-tag mr-3 w-4 text-purple-600"></i>
              <span>{event.type}</span>
            </div>
            <div className="flex items-center">
              <i className="fas fa-users mr-2 w-4 text-purple-600"></i>
              <span>{event.currentAttendees}/{event.capacity}</span>
            </div>
          </div>
        </div>

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {event.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Host Info and Price */}
        <div className="pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-white">
                  {event.hostName?.charAt(0)?.toUpperCase() || 'H'}
                </span>
              </div>
              <span className="text-sm text-slate-600">
                {event.hostBusiness}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className={`text-sm font-medium ${event.price === 'Free' ? 'text-green-600' : 'text-blue-600'}`}>
                {event.price}
              </span>
              {spotsLeft <= 5 && spotsLeft > 0 && (
                <span className="text-xs text-orange-600 font-medium">
                  {spotsLeft} left
                </span>
              )}
              {spotsLeft === 0 && (
                <span className="text-xs text-red-600 font-medium">
                  Full
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none rounded-xl"></div>
    </div>
  );
}

// Event Details Modal Component
function EventDetailsModal({ event, onClose, onRSVP }) {
  const [rsvpData, setRsvpData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const eventDate = new Date(event.date);
  const spotsLeft = event.capacity - event.currentAttendees;
  
  const handleRsvpSubmit = async (e) => {
    e.preventDefault();
    
    if (!rsvpData.name.trim() || !rsvpData.email.trim()) {
      alert('Please fill in your name and email');
      return;
    }

    setIsSubmitting(true);
    try {
      await onRSVP(event.id, rsvpData);
    } catch (error) {
      console.error('RSVP failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRsvpData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const shareEvent = (platform) => {
    const url = `https://trentconnect.com/events/${event.id}`;
    const text = `Check out this event: ${event.title} at ${event.location}`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      email: `mailto:?subject=${encodeURIComponent(event.title)}&body=${encodeURIComponent(text + '\n\n' + url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyEventUrl = () => {
    const url = `https://trentconnect.com/events/${event.id}`;
    navigator.clipboard.writeText(url);
    alert('Event link copied to clipboard!');
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 shadow-2xl">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-slate-900">Event Details</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={copyEventUrl}
              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Copy event link"
            >
              <i className="fas fa-link"></i>
            </button>
            <div className="relative group">
              <button className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                <i className="fas fa-share"></i>
              </button>
              <div className="absolute top-full right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto z-10">
                <div className="p-2 space-y-1">
                  <button onClick={() => shareEvent('facebook')} className="flex items-center w-full px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 rounded">
                    <i className="fab fa-facebook mr-2 text-blue-600"></i>
                    Facebook
                  </button>
                  <button onClick={() => shareEvent('twitter')} className="flex items-center w-full px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 rounded">
                    <i className="fab fa-twitter mr-2 text-blue-400"></i>
                    Twitter
                  </button>
                  <button onClick={() => shareEvent('linkedin')} className="flex items-center w-full px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 rounded">
                    <i className="fab fa-linkedin mr-2 text-blue-700"></i>
                    LinkedIn
                  </button>
                  <button onClick={() => shareEvent('whatsapp')} className="flex items-center w-full px-3 py-2 text-sm text-slate-700 hover:bg-green-50 rounded">
                    <i className="fab fa-whatsapp mr-2 text-green-600"></i>
                    WhatsApp
                  </button>
                  <button onClick={() => shareEvent('email')} className="flex items-center w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded">
                    <i className="fas fa-envelope mr-2 text-slate-600"></i>
                    Email
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
          {/* Event Content - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Image */}
            {event.imageUrl && (
              <div className="aspect-video bg-slate-200 rounded-lg overflow-hidden">
                <img 
                  src={event.imageUrl} 
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Event Title and Basic Info */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{event.title}</h1>
                  <div className="flex items-center space-x-2 text-slate-600">
                    <span>Hosted by</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-white">
                          {event.hostName?.charAt(0)?.toUpperCase() || 'H'}
                        </span>
                      </div>
                      <span className="font-medium">{event.hostBusiness}</span>
                    </div>
                  </div>
                </div>
                {event.featured && (
                  <span className="bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full font-medium">
                    Featured Event
                  </span>
                )}
              </div>

              {/* Event Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 rounded-lg p-4">
                <div className="flex items-center text-slate-700">
                  <i className="fas fa-calendar mr-3 text-blue-600 w-5"></i>
                  <div>
                    <div className="font-medium">{eventDate.toLocaleDateString('en-GB', { 
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long', 
                      day: 'numeric' 
                    })}</div>
                  </div>
                </div>
                
                <div className="flex items-center text-slate-700">
                  <i className="fas fa-clock mr-3 text-blue-600 w-5"></i>
                  <div>
                    <div className="font-medium">{event.time}</div>
                  </div>
                </div>
                
                <div className="flex items-center text-slate-700">
                  <i className="fas fa-map-marker-alt mr-3 text-blue-600 w-5"></i>
                  <div>
                    <div className="font-medium">{event.location}</div>
                  </div>
                </div>

                <div className="flex items-center text-slate-700">
                  <i className="fas fa-tag mr-3 text-purple-600 w-5"></i>
                  <div>
                    <div className="font-medium">{event.type}</div>
                  </div>
                </div>
                
                <div className="flex items-center text-slate-700">
                  <i className="fas fa-users mr-3 text-purple-600 w-5"></i>
                  <div>
                    <div className="font-medium">{event.currentAttendees} attending</div>
                    {spotsLeft > 0 && (
                      <div className="text-sm text-slate-500">{spotsLeft} spots left</div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center text-slate-700">
                  <i className="fas fa-pound-sign mr-3 text-green-600 w-5"></i>
                  <div>
                    <div className={`font-medium ${event.price === 'Free' ? 'text-green-600' : 'text-blue-600'}`}>
                      {event.price}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tags */}
            {event.tags && event.tags.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {event.tags.map((tag, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-2">About this event</h4>
              <p className="text-slate-700 leading-relaxed">{event.description}</p>
            </div>

            {/* What to Expect */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-2">What to Expect</h4>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <i className="fas fa-check text-green-600 mr-3 mt-0.5 flex-shrink-0"></i>
                  Professional networking opportunity
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check text-green-600 mr-3 mt-0.5 flex-shrink-0"></i>
                  Connect with local business community
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check text-green-600 mr-3 mt-0.5 flex-shrink-0"></i>
                  Refreshments provided
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check text-green-600 mr-3 mt-0.5 flex-shrink-0"></i>
                  Relaxed, friendly atmosphere
                </li>
              </ul>
            </div>

            {/* Host Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">About {event.hostBusiness}</h4>
              <p className="text-blue-800 text-sm">
                {event.hostBusiness} is a trusted local business in Nottingham, committed to bringing the community together through amazing events and experiences.
              </p>
            </div>
          </div>

          {/* RSVP Section - Right Column */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  {spotsLeft > 0 ? 'Reserve Your Spot' : 'Event Full'}
                </h3>
                
                {spotsLeft > 0 ? (
                  <form onSubmit={handleRsvpSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={rsvpData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={rsvpData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={rsvpData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Your phone number"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">
                        Message to Organizer (Optional)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={rsvpData.message}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="Any questions or dietary requirements?"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-400 text-white py-3 rounded-lg font-medium transition-all disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-calendar-check mr-2"></i>
                          {event.price === 'Free' ? 'RSVP Now - Free' : `Book Now - ${event.price}`}
                        </>
                      )}
                    </button>

                    <p className="text-xs text-slate-500 text-center">
                      You'll receive a confirmation email with event details
                    </p>
                  </form>
                ) : (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <i className="fas fa-users text-red-600 text-xl"></i>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">Event is Full</h4>
                    <p className="text-slate-600 text-sm mb-4">
                      This event has reached maximum capacity. Check out other events or follow the host for future opportunities.
                    </p>
                    <button className="w-full bg-slate-600 text-white py-3 rounded-lg font-medium cursor-not-allowed">
                      <i className="fas fa-ban mr-2"></i>
                      No Spots Available
                    </button>
                  </div>
                )}

                {/* Event Stats */}
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>Event Capacity</span>
                    <span className="font-medium">{event.capacity} people</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-600 mt-1">
                    <span>Current Attendees</span>
                    <span className="font-medium">{event.currentAttendees} people</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 mt-3">
                    <div 
                      className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${(event.currentAttendees / event.capacity) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}